import { useMemo } from "react";
import { OnboardingPage } from "./pages/OnboardingPage";
import { SignupPage } from "./pages/SignupPage";

type RouteKind = "signup" | "onboarding" | "invalid";

interface ParsedRoute {
  kind: RouteKind;
  uid: string | null;
  token: string | null;
  invalidReason: string | null;
}

/**
 * Two valid routes:
 *   /                               → redirects to /signup
 *   /signup                         → public self-onboarding entry point
 *   /onboarding/:uid?t=<token>      → resume an existing enrollment (admin
 *                                     invite or just-created self-signup)
 * Anything else renders a polite "not found" card.
 */
function parseRoute(): ParsedRoute {
  const path = window.location.pathname;
  const url = new URL(window.location.href);

  // Belt-and-suspenders with the Firebase Hosting redirect for `/`.
  if (path === "/" || path === "") {
    window.location.replace("/signup");
    return { kind: "signup", uid: null, token: null, invalidReason: null };
  }

  if (path === "/signup" || path === "/signup/") {
    return { kind: "signup", uid: null, token: null, invalidReason: null };
  }

  const m = path.match(/^\/onboarding\/([^/]+)\/?$/);
  if (!m) {
    return {
      kind: "invalid",
      uid: null,
      token: null,
      invalidReason: "This URL is not an enrollment link.",
    };
  }
  const uid = decodeURIComponent(m[1] ?? "");
  const token = url.searchParams.get("t");
  if (!uid) {
    return {
      kind: "invalid",
      uid: null,
      token: null,
      invalidReason: "Missing user id.",
    };
  }
  if (!token) {
    return {
      kind: "invalid",
      uid,
      token: null,
      invalidReason: "Missing access token.",
    };
  }
  return { kind: "onboarding", uid, token, invalidReason: null };
}

export function App() {
  const route = useMemo(parseRoute, []);

  return (
    <div className="min-h-full bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-3xl px-4 py-3 flex items-center justify-between gap-3">
          <img
            src="/bit-logo.png"
            alt="BIT - Bluetooth Interlock Technologies"
            className="h-12 sm:h-14 w-auto object-contain"
          />
          <div className="text-right leading-tight">
            <div className="text-sm font-semibold text-slate-900">
              Enrollment
            </div>
            <div className="text-[11px] text-slate-500">
              Secure setup • powered by Stripe
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
        {route.kind === "signup" ? (
          <SignupPage />
        ) : route.kind === "onboarding" && route.uid && route.token ? (
          <OnboardingPage uid={route.uid} token={route.token} />
        ) : (
          <InvalidLink reason={route.invalidReason ?? "Invalid link"} />
        )}
      </main>

      <footer className="mx-auto max-w-3xl px-4 pb-10 pt-6 text-center text-xs text-slate-400">
        Need help? Reply to your enrollment email or call AMS support.
      </footer>
    </div>
  );
}

function InvalidLink({ reason }: { reason: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h1 className="text-lg font-semibold text-slate-900">
        Enrollment link not recognized
      </h1>
      <p className="mt-2 text-sm text-slate-600">{reason}</p>
      <p className="mt-3 text-sm text-slate-600">
        Please use the link from your AMS welcome email, or{" "}
        <a
          href="/signup"
          className="font-medium text-brand-600 hover:text-brand-700 underline underline-offset-2"
        >
          start a new enrollment
        </a>
        .
      </p>
    </div>
  );
}
